/**
 * Shared rendering parts for condition renderers.
 * Matches ~/code/ui condition-editor-v2 parts (Key, Value, Times, Within, Filters).
 */

import { Box, Text } from '@customerio/pluma-components/react';
import plur from 'plur';
import * as styles from './condition-parts.css';
import { formatWithin } from '../parse/parse-helpers';
import type { ConditionFilter } from '../types';

type BoldProps = { children: React.ReactNode };

export function Bold({ children }: BoldProps) {
  return (
    <Text as="span" weight="semibold" color="bold">
      {children}
    </Text>
  );
}

type TimesProps = { times?: number };

export function Times({ times }: TimesProps) {
  if (times == null || times <= 1) return null;
  return (
    <>
      {' '}
      at least <Bold>{times}</Bold> {plur('time', times)}
    </>
  );
}

type WithinProps = { within?: number };

export function Within({ within }: WithinProps) {
  if (within == null || within === 0) return null;
  return (
    <>
      {' '}
      within <Bold>{formatWithin(within)}</Bold>
    </>
  );
}

type FieldOperatorValueProps = {
  field?: string;
  operator?: string;
  value?: string;
  value2?: string;
};

export function FieldOperatorValue({ field, operator, value, value2 }: FieldOperatorValueProps) {
  return (
    <>
      {field != null ? <Bold>{field}</Bold> : null}
      {operator != null ? <> {operator}</> : null}
      {value != null ? (
        <>
          {' '}
          <Bold>{value}</Bold>
        </>
      ) : null}
      {value2 != null ? (
        <>
          {' '}
          and <Bold>{value2}</Bold>
        </>
      ) : null}
    </>
  );
}

type SubFiltersProps = {
  filters?: ConditionFilter[];
  filtersOperator?: 'and' | 'or';
  /** Optional type label shown after the operator (e.g. "object attributes"). */
  label?: string;
};

export function SubFilters({ filters, filtersOperator, label }: SubFiltersProps) {
  if (filters == null || filters.length === 0) return null;
  const operatorLabel = filtersOperator === 'or' ? 'any' : 'all';
  return (
    <>
      {' '}
      and matches {operatorLabel}
      {label != null ? ` ${label}` : ''}:
      <Box display="flex" gap="100" alignItems="flex-start" mt="100" flexWrap="wrap">
        {filters.map((filter, index) => (
          <Text
            key={`${index}-${filter.field}-${filter.operator}`}
            size="xs"
            color="subtle"
            display="inline-block"
            p="050"
            overflowWrap="anywhere"
            className={styles.filterChip}
          >
            <Bold>{filter.field}</Bold> {filter.operator}
            {filter.value != null ? (
              <>
                {' '}
                <Bold>{filter.value}</Bold>
              </>
            ) : null}
            {filter.value2 != null ? (
              <>
                {' '}
                and <Bold>{filter.value2}</Bold>
              </>
            ) : null}
          </Text>
        ))}
      </Box>
    </>
  );
}
